import React, { useEffect, useState } from 'react';
import { Card, CardContent, Table, TableCell, TableRow, Typography, Box, Button, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';
import { HiInboxArrowDown } from "react-icons/hi2";
import { useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import authService from "../../../utils/authService";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface PerformanceData {
  ID: string;
  Name: string;
  Branch: string;
  CGPA: string | null;
  Mobile: string;
  Email: string;
  College_Rank: number;
  Overall_Rank: number;
  Meeting_attendance: string;
  No_of_hours: number;
  Delay: number;
  Practice_MCQ: number;
  Practice_Coding: number;
  Practice_Weekly_Test: number;
  Score: string;
  Final_Test_Percentage: number;
  Internship_Challenge: number;
  Internship_Challenge_Percentage: number;
  Category: string;
}

const PerformanceAnalysis: React.FC = () => {
  const location = useLocation();
  const { studentInfo } = location.state || {};
  const [studentData, setStudentData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>("Average");
  const [currentSubjectKey, setCurrentSubjectKey] = useState<string>('');
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [courses, setCourses] = useState<{ name: string }[]>([]);
  const [batches, setBatches] = useState<{ name: string }[]>([]);
  const [subjects, setSubjects] = useState<{ name: string }[]>([]);
  const navigate = useNavigate();

  // Helper function to get the first available subject key from studentData
  const getCurrentSubjectData = () => {
    if (!studentData || !studentData.data) {
      return null;
    }
    
    // Get all keys from studentData.data
    const subjectKeys = Object.keys(studentData.data);
    
    // If we have a selected subject, try to use it
    if (selectedSubject && studentData.data[selectedSubject]) {
      return studentData.data[selectedSubject];
    }
    
    // Otherwise, use the first available subject
    if (subjectKeys.length > 0) {
      const firstSubject = subjectKeys[0];
      return studentData.data[firstSubject];
    }
    
    return null;
  };

  useEffect(() => {
    if (!studentInfo?.ID) {
      navigate('/trainer/reports');
    } else {
      setIsLoading(true);
      authService.get(`analysis/${studentInfo.ID}/`)
        .then(response => {
          const fullResponse = response.data;
          const data = fullResponse.data;
          setStudentData(fullResponse);
          // Set the first available subject as default
          if (data && Object.keys(data).length > 0) {
            const firstSubjectKey = Object.keys(data)[0];
            setCurrentSubjectKey(firstSubjectKey);
          }
        })
        .catch(error => {
          console.error('Error fetching student data:', error);
          setError('Failed to load student data. Please try again later.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [studentInfo.ID, navigate]);

  useEffect(() => {
    authService.get('filters/analysis/')
      .then(response => {
        const data = response.data;
        if (data && data.Courses) {
          setCourses(data.Courses.map((course: string) => ({ name: course })));
        }
      })
      .catch(error => {
        console.error('Error fetching filters:', error);
        setError('Failed to load courses. Please try refreshing the page.');
      });
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      setIsLoading(true);
      authService.get('filters/analysis/')
        .then(response => {
          const data = response.data;
          if (data && data.batchs && data.batchs[selectedCourse]) {
            setBatches(data.batchs[selectedCourse].map((batch: string) => ({ name: batch })));
          } else {
            setBatches([]);
          }
          setSelectedBatch('');
          setSelectedSubject('');
        })
        .catch(error => {
          console.error('Error fetching batches:', error);
          setBatches([]);
          setError('Failed to load batches for the selected course.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedCourse && selectedBatch) {
      setIsLoading(true);
      authService.get('filters/analysis/')
        .then(response => {
          const data = response.data;
          if (data && data.subjects && data.subjects[selectedCourse] && data.subjects[selectedCourse][selectedBatch]) {
            setSubjects(data.subjects[selectedCourse][selectedBatch].map((subject: string) => ({ name: subject })));
          } else {
            setSubjects([]);
          }
          setSelectedSubject('');
        })
        .catch(error => {
          console.error('Error fetching subjects:', error);
          setSubjects([]);
          setError('Failed to load subjects for the selected batch.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [selectedCourse, selectedBatch]);

  // Update subjects dropdown when studentData changes
  useEffect(() => {
    if (studentData && studentData.data) {
      const availableSubjects = Object.keys(studentData.data).map(subjectKey => ({ name: subjectKey }));
      setSubjects(availableSubjects);
      if (availableSubjects.length > 0 && !selectedSubject) {
        const firstSubject = availableSubjects[0].name;
        setSelectedSubject(firstSubject);
        setCurrentSubjectKey(firstSubject);
      }
    }
  }, [studentData, selectedSubject]);

  // Update currentSubjectKey when selectedSubject changes
  useEffect(() => {
    if (selectedSubject) {
      setCurrentSubjectKey(selectedSubject);
    }
  }, [selectedSubject]);

  const handleCourseChange = (event: SelectChangeEvent<string>) => {
    setSelectedCourse(event.target.value);
    setError(null);
  };

  const handleBatchChange = (event: SelectChangeEvent<string>) => {
    setSelectedBatch(event.target.value);
    setError(null);
  };

  const handleSubjectChange = (event: SelectChangeEvent<string>) => {
    setSelectedSubject(event.target.value);
    setError(null);
  };

  const handleDownload = () => {
    if (studentData) {
      const currentSubjectData = getCurrentSubjectData();
      if (!currentSubjectData) return;
      
      const exportData = [
        {
          'Student ID': studentInfo.ID,
          'Student Name': studentInfo.Name,
          'Branch': studentInfo.Branch,
          'CGPA': studentInfo.CGPA,
          'Mobile': studentInfo.Mobile,
          'Email': studentInfo.Email,
          'Attendance': 'Good',
          'College Rank': studentData.college_rank,
          'Overall Rank': studentData.overall_rank,
          'Meeting attendance': currentSubjectData.cumulative.Meeting_attendance || 'N/A',
          'No. of hours spent': currentSubjectData.cumulative.no_of_hours || 'N/A',
          'Delay (Days)': currentSubjectData.cumulative.delays || 'N/A',
          'Practice MCQ [%]': currentSubjectData.cumulative.practice_mcq_percentage || 'N/A',
          'Practice Coding [%]': currentSubjectData.cumulative.practice_coding_percentage || 'N/A',
          'Practice Weekly test [%]': currentSubjectData.cumulative.weekly_test_percentage || 'N/A',
          'Overall [%]': currentSubjectData.cumulative.overall_percentage || 'N/A',
          'Final Test Score': currentSubjectData.cumulative.Final_Test_score || 'N/A',
          'Final Test [%]': currentSubjectData.cumulative.Final_Test_percentage || 'N/A',
          'Internship challenge': currentSubjectData.cumulative.Internship_Test_score || 'N/A',
          'Internship challenge in [%]': currentSubjectData.cumulative.Internship_Test_percentage || 'N/A',
          'Category': studentData.category || 'N/A',
        },
      ];
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Performance Data");
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(dataBlob, 'performance_analysis.xlsx');
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <Typography variant="h6" color="textSecondary">
          No student data available.
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <CardContent style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <div>
              <FormControl variant="outlined" style={{ minWidth: 150, marginRight: '16px' }}>
                <InputLabel>Course</InputLabel>
                <Select
                  value={selectedCourse}
                  onChange={handleCourseChange}
                  label="Course"
                  disabled={isLoading}
                  style={{ maxHeight: '40px', display: 'flex', alignItems: 'center' }}
                >
                  <MenuItem value="">Select Course</MenuItem>
                  {courses.map(course => (
                    <MenuItem key={course.name} value={course.name}>{course.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" style={{ minWidth: 150, marginRight: '16px' }}>
                <InputLabel>Batch</InputLabel>
                <Select
                  value={selectedBatch}
                  onChange={handleBatchChange}
                  label="Batch"
                  disabled={!selectedCourse || isLoading}
                  style={{ maxHeight: '40px', display: 'flex', alignItems: 'center' }}
                >
                  <MenuItem value="">Select Batch</MenuItem>
                  {batches.map(batch => (
                    <MenuItem key={batch.name} value={batch.name}>{batch.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" style={{ minWidth: 150 }}>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={handleSubjectChange}
                  label="Subject"
                  disabled={!studentData || isLoading}
                  style={{ maxHeight: '40px', display: 'flex', alignItems: 'center' }}
                >
                  <MenuItem value="">Select Subject</MenuItem>
                  {subjects.map(subject => (
                    <MenuItem key={subject.name} value={subject.name}>{subject.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <Button onClick={handleDownload}><HiInboxArrowDown color='black' size={25} /></Button>
          </Box>
          
          <div className="row">
            {[
              { label: 'Student ID', value: studentInfo.ID },
              { label: 'Student Name', value: studentInfo.Name },
              { label: 'Branch', value: studentInfo.Branch },
              { label: 'CGPA', value: studentInfo.CGPA },
              { label: 'Mobile', value: studentInfo.Mobile },
              { label: 'Email', value: studentInfo.Email },
              { label: 'College Rank', value: studentInfo.College_Rank },
              { label: 'Overall Rank', value: studentInfo.Overall_Rank },
              { label: 'Current Subject', value: currentSubjectKey || 'N/A' }
            ].map((item, index) => (
              <div key={index} className="col-3 mb-2">
              <span><strong>{item.label} : </strong>{item.value || '--'}</span>
          </div>
))}
          </div>

          {/* View Toggle */}
          <Box display="flex" justifyContent="flex-end" mb={3}>
            <Select
              value={selectedOption}
              onChange={(event) => setSelectedOption(event.target.value)}
              style={{ 
                minWidth: '120px',
                backgroundColor: '#ffffff',
                borderRadius: '8px'
              }}
              size="small"
            >
              <MenuItem value="Average">Average</MenuItem>
              <MenuItem value="All">All</MenuItem>
            </Select>
          </Box>
          
          <div style={{ overflowX: "auto" }}>
            {selectedOption === "Average" && (
              (() => {
                const currentSubjectData = getCurrentSubjectData();
                if (!currentSubjectData) return null;
                
                return (
                  <Table>
                    <TableRow>
                      <TableCell className='border-black border-bottom border-top border-start'>Meeting attendance</TableCell>
                      <TableCell className='border-black border-bottom border-top'>No. of hours spent</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Delay (Days)</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Practice MCQ [%]</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Practice Coding [%]</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Practice Weekly test[%]</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Overall [%]</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Final Test Score</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Final Test [%]</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Internship challenge</TableCell>
                      <TableCell className='border-black border-bottom border-top'>Internship challenge in [%]</TableCell>
                      <TableCell className='border-black border-bottom border-top border-end'>Category</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='border-black border-bottom border-start'>{currentSubjectData.cumulative.Meeting_attendance || '--'}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.no_of_hours}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.delays || '--'}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.practice_mcq_percentage}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.practice_coding_percentage}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.weekly_test_percentage}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.overall_percentage}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.Final_Test_score}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.Final_Test_percentage}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.Internship_Test_score}</TableCell>
                      <TableCell className='border-black border-bottom'>{currentSubjectData.cumulative.Internship_Test_percentage}</TableCell>
                      <TableCell className='border-black border-bottom border-end'>{studentData.category || 'N/A'}</TableCell>
                    </TableRow>
                  </Table>
                );
              })()
            )}
            
            {selectedOption === "All" && (
              (() => {
                const currentSubjectData = getCurrentSubjectData();
                if (!currentSubjectData) return null;
                
                return (
                  <Table>
                    <TableRow className="border border-black border-bottom">
                      <TableCell rowSpan={2} className="text-center border border-r border-black">Weeks</TableCell>
                      <TableCell rowSpan={2} className="text-center border border-r border-black">No. of hours spent</TableCell>
                      <TableCell colSpan={2} className="text-center border border-r border-black">Practice MCQ</TableCell>
                      <TableCell colSpan={2} className="text-center border border-r border-black">Practice Coding</TableCell>
                      <TableCell colSpan={2} className="text-center border border-r border-black">Practice Weekly test</TableCell>
                      <TableCell rowSpan={2} className='text-center border border-r border-black'>Overall [%]</TableCell>
                    </TableRow>

                    <TableRow className="text-center border border-black border-bottom">
                      <TableCell className="text-center border border-r border-black">Score</TableCell>
                      <TableCell className="text-center border border-r border-black">[%]</TableCell>
                      <TableCell className="text-center border border-r border-black">Score</TableCell>
                      <TableCell className="text-center border border-r border-black">[%]</TableCell>
                      <TableCell className="text-center border border-r border-black">Score</TableCell>
                      <TableCell className="text-center border border-r border-black">[%]</TableCell>
                    </TableRow>

                    {currentSubjectData.weeks.map((week: any, index: number) => (
                      <TableRow key={index} className="text-center border border-black">
                        <TableCell className="text-center border-r border-black">{week.week}</TableCell>
                        <TableCell className="text-center border-r border-black">{week.no_of_hours}</TableCell>
                        <TableCell className="text-center border-r border-black">{week.practice_mcq_score}</TableCell>
                        <TableCell className="text-center border-r border-black">{week.practice_mcq_percentage}</TableCell>
                        <TableCell className="text-center border-r border-black">{week.practice_coding_score}</TableCell>
                        <TableCell className="text-center border-r border-black">{week.practice_coding_percentage}</TableCell>
                        <TableCell className="text-center border-r border-black">{week.weekly_test_score}</TableCell>
                        <TableCell className="text-center border-r border-black">{week.weekly_test_percentage}</TableCell>
                        <TableCell className="text-center border-r border-black">{week.overall_percentage}</TableCell>
                      </TableRow>
                    ))}

                    {/* Category and Ranks */}
                    <TableRow className="border border-black">
                      <TableCell className="border-black border text-center" colSpan={4}>
                        Category = {studentData.category || 'N/A'}
                      </TableCell>
                      <TableCell className="border-black border text-center" colSpan={3}>
                        College Rank = {studentData.college_rank || 'N/A'}
                      </TableCell>
                      <TableCell className="border-black border text-center" colSpan={2}>
                        Overall Rank = {studentData.overall_rank || 'N/A'}
                      </TableCell>
                    </TableRow>
                  </Table>
                );
              })()
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceAnalysis;
